import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import * as XLSX from "xlsx";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { companyID } from "../../auth";
import Loader from "../Loader";
import AcknowledgmentPopup from "../../utils/AcknowledgmentPopup";
import api from "../../api/axios";
import { formatDateForSQL } from "../../utils/formatDateForSQL";

const UploadHoliday = ({
  show,
  setShow,
  handleDownload,
  holidayListDownloaded,
  setHolidayListDownloaded,
  markForRefetch,
}) => {
  const company_id = companyID();

  const [uploadMode, setUploadMode] = useState("sheet"); // "manual" or "sheet"
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [preview, setPreview] = useState(false);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState({
    show: false,
    message: "",
  });
  const [acknowledgmentPopup, setAcknowledgmentPopup] = useState({
    show: false,
    status: null,
    message: null,
    icon: null,
  });

  // Manual upload states
  const [configurationHolidays, setConfigurationHolidays] = useState([]);
  const [manualHolidayRows, setManualHolidayRows] = useState([
    { occasion: null, date: null },
  ]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showCreateHolidayModal, setShowCreateHolidayModal] = useState(false);
  const [newHolidayName, setNewHolidayName] = useState("");
  const [isSendMail, setIsSendMail] = useState(false);

  // Fetch configuration holidays for manual upload dropdown
  useEffect(() => {
    if (show && uploadMode === "manual") {
      fetchConfigurationHolidays();
    }
  }, [show, uploadMode]);

  const fetchConfigurationHolidays = async () => {
    try {
      const response = await api.get("/configuration-holidays");
      const holidays = response.data.data || response.data || [];
      setConfigurationHolidays(holidays);
    } catch (error) {
      console.error("Error fetching configuration holidays:", error);
      setAcknowledgmentPopup({
        show: true,
        status: "danger",
        message: "Failed to fetch holidays list",
        icon: "fa-regular fa-circle-xmark",
      });
      setTimeout(() => {
        setAcknowledgmentPopup({
          show: false,
          status: null,
          message: null,
          icon: null,
        });
      }, 3000);
    }
  };

  const handleClose = () => {
    setShow(false);
    setPreview(false);
    setExcelData([]);
    setFile(null);
    setHolidayListDownloaded(false);
    setUploadMode("sheet");
    setManualHolidayRows([{ occasion: null, date: null }]);
    setValidationErrors([]);
    setIsSendMail(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const filteredData = jsonData.filter((row, index) => {
        if (index === 0) return true;
        return row[0] && row[1];
      });

      setExcelData(filteredData);
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const showPreview = () => {
    setPreview(true);
  };

  const closePreview = () => {
    setPreview(false);
  };

  const handleDeleteFile = () => {
    setFile(null);
    setExcelData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setPreview(false);
    setAcknowledgmentPopup({
      show: true,
      status: "success",
      message: "File deleted. You can now upload a new file.",
      icon: "fa-regular fa-circle-check",
    });
    hideAcknowledgmentPopup(false);
  };

  const hideAcknowledgmentPopup = (closeModal = true) => {
    const resetAcknowledgmentPopupData = () => {
      setAcknowledgmentPopup({
        show: false,
        status: null,
        message: null,
        icon: null,
      });
      if (closeModal) {
        handleClose();
      }
    };
    setTimeout(() => resetAcknowledgmentPopupData(), 3000);
  };

  // Manual Upload Functions
  const handleAddRow = () => {
    const lastRow = manualHolidayRows[manualHolidayRows.length - 1];
    if (!lastRow.occasion || !lastRow.date) {
      setValidationErrors((prev) => {
        const newErrors = [...prev];
        newErrors[manualHolidayRows.length - 1] =
          "Please fill the available field for uploading holiday";
        return newErrors;
      });
      return;
    }
    setManualHolidayRows([
      ...manualHolidayRows,
      { occasion: null, date: null },
    ]);
    setValidationErrors([...validationErrors, ""]);
  };

  const handleRemoveRow = (index) => {
    if (manualHolidayRows.length === 1) return;
    const updatedRows = manualHolidayRows.filter((_, i) => i !== index);
    const updatedErrors = validationErrors.filter((_, i) => i !== index);
    setManualHolidayRows(updatedRows);
    setValidationErrors(updatedErrors);
  };

  const handleManualRowChange = (index, field, value) => {
    const updatedRows = [...manualHolidayRows];
    updatedRows[index][field] = value;
    setManualHolidayRows(updatedRows);

    // Clear validation error for this row
    const updatedErrors = [...validationErrors];
    updatedErrors[index] = "";
    setValidationErrors(updatedErrors);
  };

  const handleCreateNewHoliday = async () => {
    if (!newHolidayName.trim()) {
      toast.error("Please enter holiday name");
      return;
    }

    try {
      setLoading({ show: true, message: "Creating new holiday..." });
      // Assuming endpoint for creating holiday - adjust based on your API
      const response = await api.post("/holidays", {
        occasion: newHolidayName,
      });

      // Refresh configuration holidays
      await fetchConfigurationHolidays();

      setShowCreateHolidayModal(false);
      setNewHolidayName("");

      toast.success("Holiday created successfully");
    } catch (error) {
      console.error("Error creating holiday:", error);
      toast.error(error.response?.data?.message || "Failed to create holiday");
    } finally {
      setLoading({ show: false, message: "" });
    }
  };

  const handleManualUpload = async () => {
    const errors = [];
    let hasError = false;

    manualHolidayRows.forEach((row, index) => {
      if (!row.occasion || !row.date) {
        errors[index] = "Please fill all fields";
        hasError = true;
      } else {
        errors[index] = "";
      }
    });

    if (hasError) {
      setValidationErrors(errors);
      return;
    }

    try {
      setLoading({ show: true, message: "Adding holidays..." });

      const holidaysData = manualHolidayRows.map((row) => ({
        Occasion: row.occasion.value || row.occasion.occasion,
        HolidayDates: formatDateForSQL(row.date),
        Year: row.date.getFullYear(),
      }));

      const response = await api.post(`company/${company_id}/upload-holidays`, {
        holidays: holidaysData,
        manual_upload: 1,
        isSendMail: isSendMail ? 1 : 0,
      });

      toast.success(response.data.message || "Holidays added successfully");
      if (markForRefetch) markForRefetch();
      handleClose();
    } catch (error) {
      console.error("Error uploading holidays:", error);
      toast.error(error.response?.data?.message || "Failed to add holidays");
    } finally {
      setLoading({ show: false, message: "" });
    }
  };

  const saveHolidays = (file_path) => {
    api
      .post(`company/${company_id}/upload-holidays`, {
        file_path,
        manual_upload: 0,
      })
      .then((res) => {
        if (markForRefetch) markForRefetch();
        setAcknowledgmentPopup({
          show: true,
          status: "success",
          message: res.data.message,
          icon: "fa-regular fa-circle-check",
        });
      })
      .catch((err) => {
        console.error("add holidays", err);
        setAcknowledgmentPopup({
          show: true,
          status: "danger",
          message: "Something went wrong",
          icon: "fa-regular fa-circle-xmark",
        });
      })
      .finally(() => {
        hideAcknowledgmentPopup();
      });
  };

  const handleUpload = () => {
    if (file) {
      setLoading((prev) => ({
        ...prev,
        show: true,
        message: `Adding Holidays...`,
      }));
      const holiday_excel = new FormData();
      holiday_excel.append("holiday_excel", file);
      api
        .post(`employees/upload-holidays-list`, holiday_excel, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          saveHolidays(res.data.filepath);
        })
        .catch((err) => {
          console.error(err);
          setAcknowledgmentPopup({
            show: true,
            status: "danger",
            message: "Something went wrong",
            icon: "fa-regular fa-circle-xmark",
          });
        })
        .finally(() => {
          setLoading((prev) => ({ ...prev, show: false, message: "" }));
          hideAcknowledgmentPopup();
        });
    }
  };

  return (
    <>
      <Modal
        show={show && !showCreateHolidayModal}
        onHide={handleClose}
        centered
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Manage Holiday List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <Form.Check
              inline
              type="radio"
              label="Upload By Sheet"
              name="uploadMode"
              id="uploadBySheet"
              checked={uploadMode === "sheet"}
              onChange={() => setUploadMode("sheet")}
            />
            <Form.Check
              inline
              type="radio"
              label="Manual Upload"
              name="uploadMode"
              id="manualUpload"
              checked={uploadMode === "manual"}
              onChange={() => setUploadMode("manual")}
            />
          </div>

          {uploadMode === "sheet" && (
            <>
              <div className="d-flex justify-content-between mb-2">
                <Button variant="success" size="sm" onClick={handleDownload}>
                  <i className="fa-solid fa-file-arrow-down" /> Download
                  Holidays List
                </Button>
              </div>
              <p className="fst-italic small mb-2">
                * Download the holiday list, update it with holidays dates,
                year, and upload it back. You can preview the uploaded file
                before finalizing.
              </p>
              <Form.Group
                controlId="formFile"
                className="mb-1 d-flex align-items-baseline justify-content-between"
              >
                {file?.name ? (
                  <p>
                    <strong>Selected File:</strong> {file.name}
                  </p>
                ) : (
                  <Form.Control
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    disabled={!!file}
                  />
                )}
                {file?.name && (
                  <div className="d-flex">
                    <Button
                      onClick={preview ? closePreview : showPreview}
                      style={{ backgroundColor: "transparent", border: "none" }}
                    >
                      <i
                        className={`fa-${preview ? "regular" : "solid"} fa-eye`}
                        style={{ color: "blue" }}
                      />
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleDeleteFile}
                      style={{ backgroundColor: "transparent", border: "none" }}
                    >
                      <i
                        className="fa-solid fa-trash"
                        style={{ color: "red" }}
                      />
                    </Button>
                  </div>
                )}
              </Form.Group>
              {preview && (
                <>
                  <div>
                    <h5 className="mb-1">Preview:</h5>
                    {excelData.length > 0 ? (
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            {excelData[0].map((col, index) => (
                              <th key={index}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {excelData.slice(1).length ? (
                            excelData.slice(1).map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                  <td key={cellIndex}>{cell}</td>
                                ))}
                              </tr>
                            ))
                          ) : (
                            <tr style={{ textAlign: "center" }}>
                              <td colSpan="3">No Holidays</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    ) : (
                      <p>No valid data to display.</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {uploadMode === "manual" && (
            <>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="mb-1 text-primary d-flex align-items-center">
                      <i className="fa-solid fa-calendar-plus me-2"></i>
                      Add Holidays Manually
                    </h6>
                    <small className="text-muted">
                      Fill in the details below to add holidays
                    </small>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddRow}
                    className="d-flex align-items-center gap-1"
                    style={{
                      borderRadius: "20px",
                      padding: "5px 12px",
                      fontSize: "13px",
                    }}
                  >
                    <i className="fa-solid fa-plus" />
                    Add More
                  </Button>
                </div>

                <div>
                  {manualHolidayRows.map((row, index) => (
                    <div
                      key={index}
                      className="mb-3 p-2 border rounded position-relative"
                      style={{
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {manualHolidayRows.length > 1 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "6px",
                            right: "6px",
                            zIndex: 1,
                          }}
                        >
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            onClick={() => handleRemoveRow(index)}
                            style={{
                              width: "24px",
                              height: "24px",
                              fontSize: "14px",
                              lineHeight: "1",
                            }}
                            title="Remove this holiday"
                          >
                            <i className="fa-solid fa-times" />
                          </Button>
                        </div>
                      )}

                      <div className="row g-2">
                        <div className="col-md-6 col-12">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-semibold mb-1">
                              <i
                                className="fa-solid fa-gift text-primary me-1"
                                style={{ fontSize: "11px" }}
                              ></i>
                              Holiday Name{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="d-flex gap-1 align-items-center">
                              <div style={{ flex: 1 }}>
                                <Select
                                  value={row.occasion}
                                  onChange={(option) =>
                                    handleManualRowChange(
                                      index,
                                      "occasion",
                                      option,
                                    )
                                  }
                                  options={configurationHolidays.map(
                                    (holiday) => ({
                                      value: holiday.occasion,
                                      label: holiday.occasion,
                                      occasion: holiday.occasion,
                                    }),
                                  )}
                                  placeholder="Select holiday..."
                                  isClearable
                                  styles={{
                                    control: (base) => ({
                                      ...base,
                                      minHeight: "36px",
                                      fontSize: "14px",
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 1050,
                                    }),
                                  }}
                                />
                              </div>
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => setShowCreateHolidayModal(true)}
                                title="Create new holiday"
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  padding: "0",
                                  fontSize: "13px",
                                }}
                              >
                                <i className="fa-solid fa-plus" />
                              </Button>
                            </div>
                          </Form.Group>
                        </div>
                        <div className="col-md-6 col-12">
                          <Form.Group className="mb-0">
                            <Form.Label className="small fw-semibold mb-1">
                              <i
                                className="fa-solid fa-calendar-day text-primary me-1"
                                style={{ fontSize: "11px" }}
                              ></i>
                              Date <span className="text-danger">*</span>
                            </Form.Label>
                            <DatePicker
                              selected={row.date}
                              onChange={(date) =>
                                handleManualRowChange(index, "date", date)
                              }
                              className="form-control"
                              dateFormat="dd-MM-yyyy"
                              placeholderText="Select date"
                              popperPlacement="auto"
                              withPortal={false}
                              minDate={new Date()}
                            />
                          </Form.Group>
                        </div>
                      </div>

                      {validationErrors[index] && (
                        <div
                          className="alert alert-danger mt-2 mb-0 py-1 px-2 small"
                          role="alert"
                        >
                          <i
                            className="fa-solid fa-exclamation-circle me-1"
                            style={{ fontSize: "12px" }}
                          ></i>
                          {validationErrors[index]}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="col-12 mt-2">
                    <Form.Group className="mb-0">
                      <Form.Check
                        type="checkbox"
                        label="Notify all employees via email for urgent holidays"
                        checked={isSendMail}
                        onChange={(e) => setIsSendMail(e.target.checked)}
                        className="small"
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {uploadMode === "sheet" ? (
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={file ? false : true}
            >
              Upload File
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleManualUpload}
              disabled={manualHolidayRows.length === 0}
            >
              Add Holidays
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
        {acknowledgmentPopup.show ? (
          <AcknowledgmentPopup
            popupVariant={acknowledgmentPopup.status}
            popupMessage={acknowledgmentPopup.message}
            popupIcon={acknowledgmentPopup.icon}
          />
        ) : (
          <></>
        )}
        {acknowledgmentPopup.show && (
          <div className="modal-backdrop fade show"></div>
        )}
        {loading.show && (
          <Loader show={loading.show} loadingMessage={loading.message} />
        )}
      </Modal>

      <Modal
        show={showCreateHolidayModal}
        onHide={() => {
          setShowCreateHolidayModal(false);
          setNewHolidayName("");
        }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            <i className="fa-solid fa-plus-circle text-success me-2"></i>
            Create New Holiday
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          <Form.Group>
            <Form.Label className="mb-2 fw-semibold">
              Holiday Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter holiday name"
              value={newHolidayName}
              onChange={(e) => setNewHolidayName(e.target.value)}
              autoFocus
              onKeyPress={(e) => {
                if (e.key === "Enter" && newHolidayName.trim()) {
                  handleCreateNewHoliday();
                }
              }}
            />
            <Form.Text className="text-muted">
              <small>Press Enter to create holiday</small>
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowCreateHolidayModal(false);
              setNewHolidayName("");
            }}
          >
            <i className="fa-solid fa-times me-1"></i>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={handleCreateNewHoliday}
            disabled={!newHolidayName.trim()}
          >
            <i className="fa-solid fa-check me-1"></i>
            Create Holiday
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UploadHoliday;
